import React, { useEffect, useRef, useState } from 'react';
import StockChart from '../charts/StockChart.tsx';
import StockChartWithDrawingsCard from '../charts/StockChartWithDrawingsCard.tsx';
import screenerApi, { ScreenerStockResponse } from '../../services/screenerApi.ts';
import { ChartConfig } from '../screener/ScreenerTable.tsx';
import { isAIScreener as checkIsAI } from '../../config/screenerConfig.ts';
import '../ScannerViewDemo.css';

interface ChartGridViewProps {
    isActive: boolean;
    data: ScreenerStockResponse[];
    loading: boolean;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    chartConfig?: ChartConfig;
    screenerCode?: string;
    smaValues?: number[];
}

const ChartGridView: React.FC<ChartGridViewProps> = ({
    isActive,
    data,
    loading,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    onPageChange,
    chartConfig = { capturedPriceLine: true },
    screenerCode,
    smaValues = [],
}) => {
    const isAIScreener = checkIsAI(screenerCode);
    const [chartWidth, setChartWidth] = useState(600);
    const [chartView, setChartView] = useState<string>('CLASSIC');

    useEffect(() => {
        if (!screenerCode) return;
        screenerApi.getScreenerByCode(screenerCode)
            .then(res => setChartView(res.data.chartView ?? 'CLASSIC'))
            .catch(() => {});
    }, [screenerCode]);
    const [openPopoverIds, setOpenPopoverIds] = useState<Set<number>>(new Set());
    const chartCardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isAIScreener && data.length > 0) {
            setOpenPopoverIds(new Set(
                data.filter(stock => stock.maxUpside !== null && stock.maxUpside !== undefined && stock.maxUpside >= 6.5)
                    .map(stock => stock.id)
            ));
        }
    }, [data, isAIScreener]);

    const togglePopover = (id: number) => {
        setOpenPopoverIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const closePopover = (id: number) => {
        setOpenPopoverIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    // Update chart width based on container size
    useEffect(() => {
        if (isActive) {
            const updateChartWidth = () => {
                if (chartCardRef.current) {
                    const cardWidth = chartCardRef.current.getBoundingClientRect().width;
                    setChartWidth(cardWidth);
                }
            };

            setTimeout(updateChartWidth, 50);
            setTimeout(updateChartWidth, 200);
            setTimeout(updateChartWidth, 500);

            const resizeObserver = new ResizeObserver(() => {
                updateChartWidth();
            });

            if (chartCardRef.current) {
                resizeObserver.observe(chartCardRef.current);
            }

            window.addEventListener('resize', updateChartWidth);

            return () => {
                window.removeEventListener('resize', updateChartWidth);
                resizeObserver.disconnect();
            };
        }
    }, [isActive, data]);

    const handlePageChange = (page: number) => {
        onPageChange(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    return (
        <div className="chart-view">
            {loading ? (
                <div className="loading-message">Loading charts...</div>
            ) : (
                <>
                    <div className="chart-grid">
                        {data.map((stock, index) => (
                            <div
                                key={stock.id}
                                className="chart-card"
                                ref={index === 0 ? chartCardRef : null}
                            >
                                <div
                                    className="chart-card-body"
                                    onClick={() => closePopover(stock.id)}
                                >
                                    {screenerCode && chartView === 'DRAWING' ? (
                                        <StockChartWithDrawingsCard
                                            symbol={stock.symbol}
                                            exchange={stock.exchange}
                                            timeframe={stock.timeframe}
                                            width={chartWidth || 600}
                                            height={400}
                                            capturedPrice={`₹${stock.capturedPrice.toFixed(2)}`}
                                            capturedOn={formatDate(stock.capturedOn)}
                                            chartConfig={chartConfig}
                                            candles={stock.candles}
                                            stockId={stock.id}
                                            screenerCode={screenerCode}
                                            smaValues={smaValues}
                                            resistanceLevel={isAIScreener ? stock.resistanceLevel : null}
                                            supportLevel={isAIScreener ? stock.supportLevel : null}
                                        />
                                    ) : (
                                        <StockChart
                                            symbol={stock.symbol}
                                            exchange={stock.exchange}
                                            timeframe={stock.timeframe}
                                            width={chartWidth || 600}
                                            height={400}
                                            capturedPrice={`₹${stock.capturedPrice.toFixed(2)}`}
                                            capturedOn={formatDate(stock.capturedOn)}
                                            chartConfig={chartConfig}
                                            candles={stock.candles}
                                            smaValues={smaValues}
                                        />
                                    )}
                                </div>

                                {/* Chart card footer — action icons row */}
                                <div className="chart-card-footer">
                                    <div className="chart-card-footer-icons">
                                        {/* Info icon — Max Upside (AI screeners only) */}
                                        {isAIScreener && (
                                            <div className="chart-footer-icon-wrapper">
                                                <button
                                                    className="chart-footer-icon-btn"
                                                    title="Max Upside"
                                                    onClick={() => togglePopover(stock.id)}
                                                >
                                                    &#9432;
                                                </button>
                                                {openPopoverIds.has(stock.id) && (
                                                    <div className="chart-footer-popover">
                                                        <div className="chart-footer-popover-label">
                                                            Max Upside %
                                                            <span
                                                                className="info-icon"
                                                                title="Maximum upside move after the captured price. Tracking continues until the closing price falls below the 45-day moving average."
                                                            >
                                                                &#9432;
                                                            </span>
                                                        </div>
                                                        {stock.maxUpside !== null && stock.maxUpside !== undefined ? (
                                                            <div className={`chart-footer-popover-value ${stock.maxUpside >= 0 ? 'profit' : 'loss'}`}>
                                                                {stock.maxUpside >= 0 ? '+' : ''}{stock.maxUpside.toFixed(2)}%
                                                            </div>
                                                        ) : (
                                                            <div className="chart-footer-popover-na">N/A</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {/* Future icons go here: copy, watchlist, etc. */}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="chart-pagination">
                        <div className="pagination-info">
                            Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalItems)} of {totalItems} stocks
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>

                            {getPageNumbers().map((page) => (
                                <button
                                    key={page}
                                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                className="pagination-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ChartGridView;
